import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { X, Moon, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * UPDATE REST BANNER - Aviso para usuarios existentes
 * 
 * Muestra un banner para que usuarios existentes completen
 * el nuevo onboarding de descanso consciente
 */

const UpdateRestBanner = ({ onStartOnboarding }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkRestOnboardingStatus();
  }, []);

  const checkRestOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verificar si ya completó el onboarding de descanso
      const response = await axios.get(`${API}/me/rest-onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mostrar banner solo si NO ha completado el rest onboarding
      if (!response.data.rest_onboarding_completed) {
        // Verificar si fue descartado temporalmente en esta sesión
        const sessionDismissed = sessionStorage.getItem('rest_banner_dismissed');
        if (!sessionDismissed) {
          setVisible(true);
        }
      }
    } catch (error) {
      console.error('Error checking rest onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Guardar en sessionStorage para no mostrar en esta sesión
    sessionStorage.setItem('rest_banner_dismissed', 'true');
    setTimeout(() => setVisible(false), 300);
  };

  const handleStartOnboarding = () => {
    setVisible(false);
    onStartOnboarding?.();
  };

  if (loading || !visible) return null;

  return (
    <AnimatePresence>
      {visible && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 left-4 right-4 z-40 md:left-auto md:right-4 md:w-96"
          data-testid="update-rest-banner"
        >
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-primary/30 rounded-2xl p-5 shadow-xl shadow-primary/5">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 text-zinc-600 hover:text-zinc-400 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <Moon className="w-6 h-6 text-primary" />
                </div>
              </div>
              
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-white">Actualiza tu perfil</h3>
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                  Hemos mejorado GUARDIÁNSFIT para adaptar mejor tus pausas y descansos durante el entrenamiento.
                </p>
                
                <p className="text-xs text-zinc-500 mb-4">
                  Completa esta actualización y activa tu programa de <span className="text-primary">Descanso Consciente</span>.
                </p>
                
                <Button
                  onClick={handleStartOnboarding}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  size="sm"
                  data-testid="update-rest-start-btn"
                >
                  Actualizar ahora
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdateRestBanner;
